import React, { useEffect, useState } from "react";
import {
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { CloudArrowUp, Pencil, Trash, X } from "react-bootstrap-icons";
import docsgql from "gql/dashboard/sales/draftOrders.gql";
import { useQuery } from "@apollo/client";
import { json, useNavigate, useSearchParams } from "react-router-dom";
import axiosClient from "utils/axios";
import useAlert from "hooks/useAlert";
import { toast } from "react-toastify";
import DefaultCurrency from "components/DefaultCurrency";
import DateFormat from "components/DateFormat";
import Details from "../details/Details";
import InfiniteScroll from "react-infinite-scroll-component";
import ScrollToTop from "components/ScrollToTop";
import { useAppContext } from "contexts/AppContext";
import * as Sentry from "@sentry/react";

const DraftedSalesOrders = () => {
  // states
  const [docs, setDocs] = useState([]);
  const [localDraftDocs, setLocalDraftDocs] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchTimer, setSearchTimer] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [pageInfo, setPageInfo] = useState({
    totalPages: 0,
    totalRecords: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // hooks
  const { loading, data, error, refetch } = useQuery(docsgql, {
    variables: { orderBy: "-created_at", page: 1, search: "" },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { appState } = useAppContext();
  const alert = useAlert();

  // functions
  const search = (val) => {
    if (!searchValue && !val) return;

    setSearchValue(val);
    if (searchTimer) clearTimeout(searchTimer);
    setSearchTimer(
      setTimeout(() => {
        setDocs([]);
        setCurrentPage(1);
        refetch({
          search: val,
          page: 1,
        });
      }, 400)
    );
  };

  const next = () => {
    if (loading) return;

    if (currentPage < pageInfo.totalPages) {
      refetch({
        page: currentPage + 1,
      });
      setCurrentPage((currentPage) => currentPage + 1);
    }
  };

  const remove = (doc) => {
    alert
      .fire({
        title: `Remove ${doc.docNumber}`,
        text: "Do you want to remove this document?",
        icon: "warning",
        showDenyButton: true,
        confirmButtonText: "Cancel",
        denyButtonText: "Yes, delete it!",
      })
      .then((confirmed) => {
        if (confirmed.isDenied) {
          toast.info("Deleting...");
          if (doc.eId) {
            let local_drafts = JSON.parse(
              localStorage.getItem("drafts") || "[]"
            );
            local_drafts = local_drafts?.filter((x) => x.eId !== doc.eId);
            localStorage.setItem("drafts", JSON.stringify(local_drafts));
            setLocalDraftDocs(local_drafts);
            toast.success("Document is deleted.");
            setSelectedOrder(null);
          } else {
            axiosClient
              .delete(`/sales/salesorders/draft/?uuid=${doc.uuid}`)
              .then((data) => {
                setDocs((docs) => docs.filter((x) => x.id !== doc.id));
                toast.success("Document is deleted.");
                setSelectedOrder(null);
              })
              .catch((err) => {
                Sentry.captureException(err);
                toast.error(err.response.data.detail.error);
              });
          }
        }
      });
  };

  const uploadLocalDraft = (doc) => {
    console.log(doc)
    toast.info("Uploading...");
    const eId = doc.eId;
    delete doc.eId;
    delete doc.createdAt;
    delete doc.updatedAt;
    axiosClient
      .post("/sales/salesorders/draft/", doc)
      .then((data) => {
        let local_drafts = JSON.parse(localStorage.getItem("drafts") || "[]");
        local_drafts = local_drafts?.filter((x) => x.eId !== eId);
        localStorage.setItem("drafts", JSON.stringify(local_drafts));
        setLocalDraftDocs(local_drafts);
        toast.success("Document is Uploaded.");
        setDocs([]);
        setCurrentPage(1);
        refetch({
          page: 1,
        });
      })
      .catch((err) => {
        Sentry.captureException(err);
        toast.error(err.response.data.detail.error);
      });
  };

  // effects
  useEffect(() => {
    if (!loading && data) {
      const _docs = data.draftedDocuments.map(d => ({
        ...d,
        data: {
          ...d.data,
          customer: appState.resources.customers.find(
            (x) => x.id == d.data.customer
          )
        }
      }));

      setDocs((docs) => [...docs, ..._docs]);
      if (data.draftedDocuments?.length) {
        setPageInfo(data.draftedDocuments[0].pageInfo);
      }
    }
  }, [loading]);

  useEffect(() => {
    if (docs.length && searchParams.get("open")) {
      setSelectedOrder(docs.find((x) => x.id == searchParams.get("open")));
      setSearchParams({});
    }
  }, [docs]);

  useEffect(() => {
    if (currentPage && pageInfo && currentPage === pageInfo?.totalPages) {
      setHasMore(false);
    }
  }, [currentPage, pageInfo]);

  useEffect(() => {
    setLocalDraftDocs(JSON.parse(localStorage.getItem("drafts") || "[]"));
  }, []);

  return (
    <React.Fragment>
      <ScrollToTop />
      {selectedOrder && (
        <Details
          order={selectedOrder}
          setOrder={setSelectedOrder}
          edit={(x) =>
            navigate(
              `/dashboard/sales/orders/form${x.documentId ? "/" + x.documentId : ""
              }?${x.eId ? "e-id=" + x.eId : "c-id=" + x.uuid}`
            )
          }
          remove={remove}
        />
      )}
      <InfiniteScroll
        dataLength={docs?.length || 0}
        className="overflow-hidden"
        next={next}
        hasMore={hasMore}
        loader={
          <Row className="justify-content-center">
            {loading && <Spinner className="w-auto" />}
          </Row>
        }
        endMessage={<p className="text-muted text-center">No more data...</p>}
      >
        <Container fluid className="mt-2">
          <Row>
            <InputGroup className="mb-3">
              <Form.Control
                value={searchValue}
                onChange={(e) => search(e.target.value)}
                placeholder="Search..."
                className="w-auto"
              />
              <X
                className="w-auto h-100 border rounded-right"
                onClick={() => search("")}
                size={20}
              />
            </InputGroup>
          </Row>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Total Sales Value</th>
                <th>Date</th>
                <th>Last Modified</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...localDraftDocs, ...docs]
                ?.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
                .map((x, index) => (
                  <tr key={x?.id}>
                    <td>{index + 1}</td>
                    <td>
                      {x?.eId
                        ? (appState?.resources?.customers?.filter(
                          (customer) => customer?.id == x.customer
                        )?.[0]?.name || "") + " (Local Draft)"
                        : x.data?.customer?.name}
                    </td>
                    <td>
                      <DefaultCurrency
                        value={
                          x.eId
                            ? x?.details?.reduce(
                              (pre, cur) =>
                                pre + cur.quantity * cur.sales_price,
                              0
                            )
                            : x?.data?.details?.reduce(
                              (pre, cur) =>
                                pre + cur.quantity * cur.sales_price,
                              0
                            )
                        }
                      />
                    </td>
                    <td>
                      <DateFormat value={x.createdAt} />
                    </td>
                    <td>
                      <DateFormat value={x.updatedAt} />
                    </td>
                    <td className="text-center">
                      <Pencil
                        size={20}
                        color="darkorange"
                        className="mx-2 cursor-pointer"
                        onClick={() =>
                          navigate(
                            `/dashboard/sales/orders/form${x.documentId ? "/" + x.documentId : ""
                            }?${x.eId ? "e-id=" + x.eId : "c-id=" + x.uuid}`
                          )
                        }
                      />
                      <Trash
                        onClick={() => remove(x)}
                        size={20}
                        color="darkred"
                        className="mx-2 cursor-pointer"
                      />
                      {x?.eId && (
                        <CloudArrowUp
                          onClick={() =>
                            uploadLocalDraft({
                              ...x,
                              doc_date: new Date().toISOString().split("T")[0],
                            })
                          }
                          size={20}
                          color="darkblue"
                          className="mx-2 cursor-pointer"
                        />
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Container>
      </InfiniteScroll>
    </React.Fragment>
  );
};

export default DraftedSalesOrders;
